const BASE = "";

export async function api<T = any>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = localStorage.getItem("vista_token");
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const resp = await fetch(`${BASE}${path}`, { ...opts, headers, credentials: "include" });
  if (resp.status === 401) {
    localStorage.removeItem("vista_token");
    throw new Error("Unauthorized");
  }
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: resp.statusText }));
    throw new Error(err.error || "Request failed");
  }
  return resp.json();
}

export function post<T = any>(path: string, body: any): Promise<T> {
  return api(path, { method: "POST", body: JSON.stringify(body) });
}

export function fmt(val: number | string): string {
  const n = typeof val === "string" ? parseFloat(val) || 0 : val;
  if (Math.abs(n) >= 1000) return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  if (Math.abs(n) >= 1) return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return "$" + n.toFixed(4);
}

export const COLORS = [
  "#3b82f6", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6",
  "#06b6d4", "#ec4899", "#f97316", "#84cc16", "#6366f1",
  "#14b8a6", "#e11d48", "#a855f7", "#eab308", "#22d3ee",
];

// Known Marketplace product codes → friendly names
const MARKETPLACE_NAMES: Record<string, string> = {
  "6e0gln1ap37v6rog0numkn0dqt": "MongoDB Atlas (Marketplace)",
};

// AWS service codes that need better display names
const SERVICE_NAMES: Record<string, string> = {
  "AmazonES": "OpenSearch",
  "AmazonGuardDuty": "GuardDuty",
  "AWSCloudTrail": "CloudTrail",
  "AWSConfig": "Config",
  "AWSDirectConnect": "Direct Connect",
  "AWSGlobalAccelerator": "Global Accelerator",
  "AWSNetworkFirewall": "Network Firewall",
  "AWSSecurityHub": "Security Hub",
  "AWSSystemsManager": "Systems Manager",
  "AWSTransfer": "Transfer Family",
  "AmazonOpenSearchService": "OpenSearch",
  "ElasticMapReduce": "EMR",
  "AmazonKinesisFirehose": "Kinesis Firehose",
  "AmazonMWAA": "MWAA (Airflow)",
  "AmazonSageMaker": "SageMaker",
  "AmazonEKS": "EKS",
  "AmazonECS": "ECS",
  "AmazonMSK": "MSK (Kafka)",
  "AmazonDocDB": "DocumentDB",
  "AmazonNeptune": "Neptune",
  "AmazonQuickSight": "QuickSight",
  "AmazonKinesis": "Kinesis",
  "AmazonElastiCache": "ElastiCache",
  "AmazonCloudFront": "CloudFront",
  "AmazonDynamoDB": "DynamoDB",
  "AmazonRoute53": "Route 53",
  "AmazonVPC": "VPC",
  "AmazonRDS": "RDS",
  "AmazonEC2": "EC2",
  "AmazonS3": "S3",
  "AWSLambda": "Lambda",
  "AmazonCloudWatch": "CloudWatch",
  "AWSCodePipeline": "CodePipeline",
  "AWSCodeBuild": "CodeBuild",
  "AWSELB": "ELB",
  "AmazonECR": "ECR",
  "AmazonSNS": "SNS",
  "AmazonSQS": "SQS",
  "AWSSecretsManager": "Secrets Manager",
  "AWSKeyManagementService": "KMS",
  "AmazonCognito": "Cognito",
  "AWSWAFv2": "WAF",
};

export function shortSvc(s: string): string {
  if (!s) return s;
  // Check marketplace mapping
  if (MARKETPLACE_NAMES[s]) return MARKETPLACE_NAMES[s];
  // Check known service names
  if (SERVICE_NAMES[s]) return SERVICE_NAMES[s];
  // Detect hash-like marketplace product codes
  if (/^[a-z0-9]{20,}$/.test(s)) return `Marketplace (${s.slice(0, 8)}...)`;
  // Default: strip Amazon/AWS prefix
  return s.replace("Amazon", "").replace("AWS", "").trim() || s;
}

export function extractName(resourceId: string, service: string): string {
  if (!resourceId) return "—";
  if (resourceId.includes(":db:")) return resourceId.split(":db:").pop()!;
  if (resourceId.includes(":cluster:")) return resourceId.split(":cluster:").pop()!;
  if (/^i-[0-9a-f]+$/.test(resourceId)) return resourceId;
  if (service === "AmazonS3" && !resourceId.includes(":")) return resourceId;
  if (resourceId.includes(":distribution/")) return "CF " + resourceId.split(":distribution/").pop()!;
  if (resourceId.includes(":cluster/")) return resourceId.split(":cluster/").pop()!;
  if (resourceId.includes(":domain/")) return resourceId.split(":domain/").pop()!;
  if (resourceId.includes(":function:")) return resourceId.split(":function:").pop()!;
  if (resourceId.includes(":loadbalancer/")) return resourceId.split(":loadbalancer/").pop()!.split("/").pop()!;
  if (/^nat-[0-9a-f]+$/.test(resourceId)) return resourceId;
  if (/^vol-[0-9a-f]+$/.test(resourceId)) return resourceId;
  const parts = resourceId.split(/[/:]/);
  const last = parts[parts.length - 1];
  if (last && last.length > 3) return last;
  return resourceId.length > 40 ? "..." + resourceId.slice(-35) : resourceId;
}
